import React, { useRef, useState, useEffect } from 'react'

import { getGenres, createGenre, updateGenre, deleteGenre } from '../../api/genres';
import { createRole, deleteRole, getRoles } from '../../api/roles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CircularProgress, Alert, Link, Snackbar } from '@mui/material'
import Pagination from '../Paginations';
const AdminRolesGenres = () => {

    const [tab, setTab] = useState(1)
    const [currentPage, setCurrentPage] = useState(1)
    const [name, setName] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [snackbarSeverity, setSnackbarSeverity] = useState('error')
    const [deletingItemId, setDeletingItemId] = useState(null)
    const successBtn = useRef()
    const itemsPerPage = 25
    const [roles, setRoles] = useState([])
    const [genres, setGenres] = useState([])

    const queryClient = useQueryClient()

    // Fetch Roles
    const { 
        data: rolesData = [], 
        isLoading: isLoadingRoles, 
        error: rolesError 
    } = useQuery({
        queryKey: ['roles'],
        queryFn: getRoles,
        select: (data) => data.artistRoles.sort((a, b) => a.name.localeCompare(b.name))
    })

    // Fetch Genres
    const { 
        data: genresData = [], 
        isLoading: isLoadingGenres, 
        error: genresError 
    } = useQuery({
        queryKey: ['genres'],
        queryFn: getGenres,
        select: (data) => data.genres
    })

    // Add Role Mutation
    const { mutate: addRole, isPending: isAddingRole } = useMutation({
        mutationFn: createRole,
        onSuccess: () => {
            queryClient.invalidateQueries(['roles'])
            setName('')
            successBtn.current.click()
        },
        onError: (error) => {
            setErrorMessage(error.message)
            setOpenSnackbar(true)
        }
    })

    // Add Genre Mutation
    const { mutate: addGenre, isPending: isAddingGenre } = useMutation({
        mutationFn: createGenre,
        onSuccess: () => {
            queryClient.invalidateQueries(['genres'])
            setName('')
            successBtn.current.click()
        },
        onError: (error) => {
            setErrorMessage(error.message)
            setOpenSnackbar(true)
        }
    })

    // Delete Role Mutation
    const deleteRoleMutation = useMutation({
        mutationFn: deleteRole,
        onSuccess: () => {
            queryClient.invalidateQueries(['roles'])
            setSuccessMessage('Role deleted successfully')
            setSnackbarSeverity('success')
            setOpenSnackbar(true)
            setDeletingItemId(null)
        },
        onError: (error) => {
            setErrorMessage(error.message)
            setSnackbarSeverity('error')
            setOpenSnackbar(true)
            setDeletingItemId(null)
        }
    })

    // Delete Genre Mutation
    const deleteGenreMutation = useMutation({
        mutationFn: deleteGenre,
        onSuccess: () => {
            queryClient.invalidateQueries(['genres'])
            setSuccessMessage('Genre deleted successfully')
            setSnackbarSeverity('success')
            setOpenSnackbar(true)
            setDeletingItemId(null)
        },
        onError: (error) => {
            setErrorMessage(error.message)
            setSnackbarSeverity('error')
            setOpenSnackbar(true)
            setDeletingItemId(null)
        }
    })

    // Pagination calculations
    const currentItems = tab === 1 
        ? rolesData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
        : genresData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    
    const totalPages = Math.ceil((tab === 1 ? rolesData.length : genresData.length) / itemsPerPage)

    const handleSearch = (event) => {
        const term = event.target.value;

        setCurrentPage(1)

        if (tab === 1) {
            const filteredResults = rolesData.filter((role) => {
              const name = role.name;
              const lowerCaseTerm = term.toLowerCase();
        
              // Check if the item's properties contain the search term
              return (
                name && name.toLowerCase().includes(lowerCaseTerm)
              );
            });
        
            setRoles(filteredResults);
            } else {
            const filteredResults = genresData.filter((role) => {
              const name = role.name;
              const lowerCaseTerm = term.toLowerCase();
        
              // Check if the item's properties contain the search term
              return (
                name && name.toLowerCase().includes(lowerCaseTerm)
              );
            });
        
            setGenres(filteredResults);
        }
    
    };

    useEffect(() => {
        // Import Bootstrap Modal dynamically to ensure it's available
        const bootstrap = window.bootstrap;
        
        // Get the success modal element
        const successModalEl = document.getElementById('addSuccessModal');
        
        if (successModalEl && bootstrap) {
            // Handle modal cleanup when it's hidden
            successModalEl.addEventListener('hidden.bs.modal', () => {
                // Remove the modal backdrop if it exists
                const backdrop = document.querySelector('.modal-backdrop');
                if (backdrop) {
                    backdrop.remove();
                }
                // Remove modal-open class from body
                document.body.classList.remove('modal-open');
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            });
        }
        
        return () => {
            // Cleanup event listener
            if (successModalEl && bootstrap) {
                successModalEl.removeEventListener('hidden.bs.modal', () => {});
            }
        };
    }, []);

    return (
        <div className="admin-page py-5">
            <div className='container'>
                <h1 className='dash-title'>Roles / Genres</h1>
                <div className="row">
                    <div className="col-12 mt-4">
                        <div className="dash-card">
                            <div className="dash-card-heading mb-2">
                                <h2 className="dash-card-title">Roles / Genres</h2>
                                <button 
                                    className='dash-card-heading-btn' 
                                    data-bs-toggle="modal" 
                                    data-bs-target="#addModal"
                                >
                                    <i className="fi fi-sr-plus"></i>
                                    Add {tab === 1 ? 'Role' : 'Genre'}
                                </button>
                            </div>

                            <div className="dash-tabs">
                                <Link 
                                    to={'#'} 
                                    className={`dash-tab ${tab === 1 && 'active'}`}
                                    onClick={() => {
                                        setTab(1)
                                        setCurrentPage(1)
                                    }}
                                >Roles</Link>
                                <Link 
                                    to={'#'} 
                                    className={`dash-tab ${tab === 2 && 'active'}`}
                                    onClick={() => {
                                        setTab(2)
                                        setCurrentPage(1)
                                    }}
                                >Genres</Link>
                            </div>

                            {/* Loading States */}
                            {((tab === 1 && isLoadingRoles) || (tab === 2 && isLoadingGenres)) && (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
                                    <CircularProgress />
                                </div>
                            )}

                            {/* Error States */}
                            {((tab === 1 && rolesError) || (tab === 2 && genresError)) && (
                                <Alert 
                                    severity="error" 
                                    sx={{ margin: '20px 0' }}
                                >
                                    {tab === 1 ? rolesError.message : genresError.message}
                                </Alert>
                            )}

                            {/* Data Table */}
                            {!isLoadingRoles && !isLoadingGenres && !rolesError && !genresError && (
                                <>
                                    <div className="table-responsive mt-3">
                                        <table className="table dash-table">
                                            <thead>
                                                <tr>
                                                    <td>{tab === 1 ? 'Role' : 'Genre'}</td>
                                                    <td>Action</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentItems.map((item, i) => (
                                                    <tr key={i}>
                                                        <td>
                                                            <div className="table-item">
                                                                {item.name}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="table-item d-flex">
                                                                <button 
                                                                    className="table-action-btn a-red me-1"
                                                                    onClick={() => {
                                                                        if (tab === 1) {
                                                                            setDeletingItemId(item.role_id)
                                                                            deleteRoleMutation.mutate(item.role_id)
                                                                        } else {
                                                                            setDeletingItemId(item.genre_id)
                                                                            deleteGenreMutation.mutate(item.genre_id)
                                                                        }
                                                                    }}
                                                                    disabled={
                                                                        (tab === 1 && deletingItemId === item.role_id && deleteRoleMutation.isPending) || 
                                                                        (tab === 2 && deletingItemId === item.genre_id && deleteGenreMutation.isPending)
                                                                    }
                                                                >
                                                                    {(tab === 1 && deletingItemId === item.role_id && deleteRoleMutation.isPending) || 
                                                                     (tab === 2 && deletingItemId === item.genre_id && deleteGenreMutation.isPending) ? (
                                                                        <CircularProgress size={16} color="inherit" />
                                                                    ) : (
                                                                        <i className="fi fi-rr-trash"></i>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <Pagination 
                                        currentPage={currentPage} 
                                        totalPages={totalPages} 
                                        handlePageChange={setCurrentPage} 
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Modal */}
            <div className="modal fade" id="addModal" tabIndex="-1" data-bs-backdrop="static">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body">
                            <div className="modal-top mb-4">
                                <h5>Add {tab === 1 ? 'Role' : 'Genre'}</h5>
                                <i className="fi fi-rr-cross" data-bs-dismiss="modal"></i>
                            </div>
                            <div className="row">
                                <div className="col-12 mt-3">
                                    <div className="dash-form-group">
                                        <label>Name</label>
                                        <input 
                                            type="text" 
                                            value={name} 
                                            onChange={(e) => setName(e.target.value)} 
                                            className='dash-form-input' 
                                        />
                                    </div>
                                </div>
                            </div>
                            {errorMessage && (
                                <h6 className='text-center text-danger mt-3'>{errorMessage}</h6>
                            )}
                            <div className="d-flex justify-content-end mt-4">
                                <button 
                                    className='dash-form-btn'
                                    onClick={() => {
                                        if (tab === 1) {
                                            addRole({ name })
                                        } else {
                                            addGenre({ name })
                                        }
                                    }}
                                    disabled={isAddingRole || isAddingGenre}
                                >
                                    {isAddingRole || isAddingGenre ? 'Adding...' : 'Submit'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Success Modal */}
            <button ref={successBtn} data-bs-toggle="modal" data-bs-target="#addSuccessModal" hidden></button>
            <div className="modal fade" id="addSuccessModal" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body success-modal"><i className="fi fi-rr-cross success-close-btn" data-bs-dismiss="modal" aria-label="Close"></i>
                            <i className="fi fi-rr-check-circle text-success success-icon"></i>
                            <h5>{tab === 1 ? 'Role' : 'Genre'} Saved!</h5>
                        </div>
                    </div>
                </div>
            </div>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                    onClose={() => setOpenSnackbar(false)}
                >
                    {errorMessage || successMessage}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default AdminRolesGenres